import { getSupabaseClient, API_URL, publicAnonKey } from './supabaseClient';
import { Project } from '../types';

const supabase = getSupabaseClient();

// Helper to get access token
async function getAccessToken(): Promise<string | null> {
  try {
    // First try to get the current session
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Error getting session:', error);
      return null;
    }
    
    if (session?.access_token) {
      return session.access_token;
    }
    
    // If no session, try to refresh
    console.log('No active session found, attempting to refresh...');
    const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
    
    if (refreshError) {
      console.error('Error refreshing session:', refreshError);
      return null;
    }
    
    return refreshedSession?.access_token || null;
  } catch (error) {
    console.error('Error in getAccessToken:', error);
    return null;
  }
}

// Helper to create headers with dual-token approach
async function createAuthHeaders(includeContentType = false): Promise<HeadersInit> {
  const token = await getAccessToken();
  if (!token) {
    console.error('❌ No access token available - user may need to log in again');
    throw new Error('Not authenticated');
  }
  
  const headers: HeadersInit = {
    'Authorization': `Bearer ${publicAnonKey}`, // Dummy token for Supabase
    'X-Auth-Token': token, // Real user token
  };
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
}

// Projects API
export async function fetchProjects(): Promise<Project[]> {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/projects`, {
    headers,
    signal: AbortSignal.timeout(8000), // 8 second timeout for faster UX
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch projects');
  }

  const data = await response.json();
  return data.projects || [];
}

export async function createProject(project: Project): Promise<Project> {
  const headers = await createAuthHeaders(true);
  const response = await fetch(`${API_URL}/projects`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ project }),
    signal: AbortSignal.timeout(10000), // 10 second timeout for create
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create project');
  }

  const data = await response.json();
  return data.project;
}

export async function getProject(projectId: string): Promise<Project | null> {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/projects/${projectId}`, {
    headers,
    signal: AbortSignal.timeout(8000), // 8 second timeout
  });

  if (!response.ok) {
    if (response.status === 404) return null;
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch project');
  }

  const data = await response.json();
  return data.project;
}

export async function updateProject(project: Project): Promise<Project> {
  const headers = await createAuthHeaders(true);
  const response = await fetch(`${API_URL}/projects/${project.id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ project }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update project');
  }

  const data = await response.json();
  return data.project;
}

export async function deleteProject(projectId: string): Promise<void> {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/projects/${projectId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete project');
  }
}

// Subscription API
export async function getSubscription() {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/subscription`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch subscription');
  }

  const data = await response.json();
  return data.subscription;
}

// Credits API
export async function getCredits() {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/credits`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch credits');
  }

  const data = await response.json();
  return data.credits;
}

export async function useCredits(amount: number, action: string, metadata?: any) {
  const headers = await createAuthHeaders(true);
  const response = await fetch(`${API_URL}/credits/use`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ amount, action, metadata }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to use credits');
  }

  const data = await response.json();
  return data;
}

export async function addCredits(amount: number, packageType: string, paymentId?: string) {
  const headers = await createAuthHeaders(true);
  const response = await fetch(`${API_URL}/credits/add`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ amount, package: packageType, paymentId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add credits');
  }

  const data = await response.json();
  return data;
}

export async function getCreditHistory() {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/credits/history`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch credit history');
  }

  const data = await response.json();
  return data;
}

// Stripe API
export async function createStripeCheckoutSession(packageId: string, credits: number, amount: number, returnPath?: string) {
  const headers = await createAuthHeaders(true);
  const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ packageId, credits, amount, returnPath }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create checkout session');
  }

  const data = await response.json();
  return data;
}

export async function verifyStripeSession(sessionId: string) {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/stripe/verify-session/${sessionId}`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify session');
  }

  const data = await response.json();
  return data;
}

// Get user invoices
export async function getInvoices() {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/invoices`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch invoices');
  }

  const data = await response.json();
  return data.invoices;
}

// Migrate old invoices from Stripe
export async function migrateInvoices() {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/migrate-invoices`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to migrate invoices');
  }

  const data = await response.json();
  return data;
}

// Sync invoice refund status from Stripe
export async function syncInvoiceStatus() {
  const headers = await createAuthHeaders();
  const response = await fetch(`${API_URL}/sync-invoice-status`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to sync invoice status');
  }

  const data = await response.json();
  return data;
}